/**
 * Utility functions for formatting values for display
 */

/**
 * Formats a decimal number to a specified number of decimal places
 * @param value - The number to format
 * @param decimals - The number of decimal places to show
 * @returns Formatted string or 'N/A' if value is null/undefined
 */
export function formatDecimal(value: unknown | null | undefined, decimals: number): string {
  if (value === null || value === undefined) return 'N/A';
  return Number(value).toFixed(decimals);
}

/**
 * Formats a percentage value
 * @param value - The percentage value (0-100)
 * @param decimals - The number of decimal places to show (default: 1)
 * @returns Formatted percentage string or 'N/A' if value is null/undefined
 */
export function formatPercentage(value: number | null | undefined, decimals = 1): string {
  if (value === null || value === undefined) return 'N/A';
  return `${value.toFixed(decimals)}%`;
}

/**
 * Formats a voltage value
 * @param value - The voltage value
 * @param decimals - The number of decimal places to show (default: 2)
 * @returns Formatted voltage string or 'N/A' if value is null/undefined
 */
export function formatVoltage(value: number | null | undefined, decimals = 2): string {
  if (value === null || value === undefined) return 'N/A';
  return `${value.toFixed(decimals)}V`;
}

/**
 * Formats an RPM value
 * @param value - The RPM value
 * @returns Formatted RPM string or 'N/A' if value is null/undefined
 */
export function formatRpm(value: number | null | undefined): string {
  if (value === null || value === undefined) return 'N/A';
  return `${value.toFixed(0)} RPM`;
}

/**
 * Formats a boolean status as a readable string
 * @param value - The boolean value
 * @param trueLabel - Label for true value (default: 'Active')
 * @param falseLabel - Label for false value (default: 'Inactive')
 * @returns Formatted status string or 'N/A' if value is null/undefined
 */
export function formatBooleanStatus(
  value: boolean | null | undefined,
  trueLabel = 'Active',
  falseLabel = 'Inactive',
): string {
  if (value === null || value === undefined) return 'N/A';
  return value ? trueLabel : falseLabel;
}

export function formatJSON(
  value: unknown,
  newLineForEachProperty = false,
  maxDecimals = 3,
  divider = ' | ',
): string {
  if (value === null || value === undefined) return 'N/A';
  try {
    if (typeof value !== 'object') {
      // Format numbers with maxDecimals if it's a number
      if (typeof value === 'number') {
        return value.toFixed(maxDecimals);
      }
      return String(value);
    }

    const formatValue = (
      obj: Record<string, unknown>,
      newLineForEachProperty = false,
      maxDecimals = 3,
      divider = ' | ',
      indent = '',
    ): string => {
      const entries = Object.entries(obj);
      if (entries.length === 0) return '{}';

      const separator = newLineForEachProperty ? '\n' : divider;
      const formattedEntries = entries.map(([key, val]) => {
        if (val && typeof val === 'object' && !Array.isArray(val)) {
          const nestedValue = formatValue(
            val as Record<string, unknown>,
            newLineForEachProperty,
            maxDecimals,
            divider,
            `${indent}  `,
          );
          return newLineForEachProperty
            ? `${key} consists of:${nestedValue}`
            : `${key} consists of: ${nestedValue}`;
        }

        // Format the value, applying maxDecimals to numbers
        let formattedVal: string;
        if (val === null) {
          formattedVal = 'null';
        } else if (typeof val === 'number') {
          formattedVal = val.toFixed(maxDecimals);
        } else {
          formattedVal = String(val);
        }

        return `${key}: ${formattedVal}`;
      });

      return newLineForEachProperty
        ? formattedEntries.map((entry) => `${indent}${entry}`).join('\n')
        : formattedEntries.join(separator);
    };

    return formatValue(
      value as Record<string, unknown>,
      newLineForEachProperty,
      maxDecimals,
      divider,
    );
  } catch (error) {
    return 'Invalid JSON';
  }
}

/**
 * Formats a 3D position object
 * @param position - The position object with x, y, z coordinates
 * @param decimals - The number of decimal places to show (default: 3)
 * @returns Formatted position string or 'N/A' if position is null/undefined
 */
export function formatPosition3D(
  position: { x: number; y: number; z: number } | null | undefined,
  decimals = 3,
): string {
  if (!position || position.x === null || position.y === null || position.z === null) {
    return 'N/A';
  }
  return `X: ${position.x.toFixed(decimals)}, Y: ${position.y.toFixed(decimals)}, Z: ${position.z.toFixed(decimals)}`;
}

/**
 * Formats a quaternion orientation object
 * @param orientation - The orientation object with x, y, z, w quaternion components
 * @param decimals - The number of decimal places to show (default: 4)
 * @returns Formatted orientation string or 'N/A' if orientation is null/undefined
 */
export function formatOrientation(
  orientation: { x: number; y: number; z: number; w: number } | null | undefined,
  decimals = 4,
): string {
  if (
    !orientation ||
    orientation.x === null ||
    orientation.y === null ||
    orientation.z === null ||
    orientation.w === null
  ) {
    return 'N/A';
  }
  return `X: ${orientation.x.toFixed(decimals)}, Y: ${orientation.y.toFixed(decimals)}, Z: ${orientation.z.toFixed(decimals)}, W: ${orientation.w.toFixed(decimals)}`;
}

/**
 * Formats a velocity object with linear and angular components
 * @param velocity - The velocity object with linear and angular 3D vectors
 * @param decimals - The number of decimal places to show (default: 3)
 * @returns Formatted velocity string or 'N/A' if velocity is null/undefined
 */
export function formatVelocity(
  velocity:
    | {
        linear: { x: number; y: number; z: number };
        angular: { x: number; y: number; z: number };
      }
    | null
    | undefined,
  decimals = 3,
): string {
  if (!velocity || !velocity.linear || !velocity.angular) {
    return 'N/A';
  }

  const linear = `Linear: ${formatPosition3D(velocity.linear, decimals)}`;
  const angular = `Angular: ${formatPosition3D(velocity.angular, decimals)}`;
  return `${linear} | ${angular}`;
}

/**
 * Formats geographic coordinates
 * @param coordinates - Array of [longitude, latitude] or [latitude, longitude]
 * @param decimals - The number of decimal places to show (default: 6)
 * @param format - The format style ('lat_lng' or 'lng_lat')
 * @returns Formatted coordinates string or 'N/A' if coordinates are null/undefined
 */
export function formatCoordinates(
  coordinates: [number, number] | null | undefined,
  decimals = 6,
  format: 'lat_lng' | 'lng_lat' = 'lng_lat',
): string {
  if (!coordinates || coordinates.length !== 2) {
    return 'N/A';
  }

  const [first, second] = coordinates;
  if (format === 'lat_lng') {
    return `Lat: ${second.toFixed(decimals)}, Lng: ${first.toFixed(decimals)}`;
  }
  return `Lng: ${first.toFixed(decimals)}, Lat: ${second.toFixed(decimals)}`;
}

/**
 * Formats a timestamp or date object
 * @param timestamp - The timestamp (Date object, string, or number)
 * @param includeTime - Whether to include time information (default: true)
 * @returns Formatted timestamp string or 'N/A' if timestamp is null/undefined
 */
export function formatTimestamp(
  timestamp: Date | string | number | null | undefined,
  includeTime = true,
): string {
  if (!timestamp) {
    return 'N/A';
  }

  try {
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) {
      return 'Invalid Date';
    }

    if (includeTime) {
      return date.toLocaleString();
    }
    return date.toLocaleDateString();
  } catch {
    return 'Invalid Date';
  }
}

/**
 * Smart formatter that detects the data type and applies appropriate formatting
 * @param value - The value to format
 * @param key - Optional key to help determine formatting context
 * @returns Formatted string representation of the value
 */
export function formatSmartValue(value: unknown, key?: string): string {
  if (value === null || value === undefined) {
    return 'N/A';
  }

  // Handle primitive types
  if (typeof value === 'boolean') {
    return formatBooleanStatus(value);
  }

  if (typeof value === 'number') {
    // Check if the key suggests a specific format
    if (key) {
      const lowerKey = key.toLowerCase();
      if (lowerKey.includes('percentage') || lowerKey.includes('level')) {
        return formatPercentage(value);
      }
      if (lowerKey.includes('voltage')) {
        return formatVoltage(value);
      }
      if (lowerKey.includes('rpm')) {
        return formatRpm(value);
      }
    }

    // Default number formatting
    return formatDecimal(value, 3);
  }

  if (typeof value === 'string') {
    return value;
  }

  if (value instanceof Date) {
    return formatTimestamp(value);
  }

  // Handle object types
  if (typeof value === 'object' && value !== null) {
    const obj = value as Record<string, unknown>;

    // Check for position-like objects
    if (
      'x' in obj &&
      'y' in obj &&
      'z' in obj &&
      typeof obj.x === 'number' &&
      typeof obj.y === 'number' &&
      typeof obj.z === 'number'
    ) {
      // Check if it's an orientation (has w component)
      if ('w' in obj && typeof obj.w === 'number') {
        return formatOrientation(obj as { x: number; y: number; z: number; w: number });
      }
      return formatPosition3D(obj as { x: number; y: number; z: number });
    }

    // Check for velocity-like objects
    if ('linear' in obj && 'angular' in obj) {
      return formatVelocity(
        obj as {
          linear: { x: number; y: number; z: number };
          angular: { x: number; y: number; z: number };
        },
      );
    }

    // Check for coordinate arrays
    if (
      Array.isArray(value) &&
      value.length === 2 &&
      typeof value[0] === 'number' &&
      typeof value[1] === 'number'
    ) {
      return formatCoordinates(value as [number, number]);
    }

    // Fallback to JSON formatting
    return formatJSON(value, false, 3);
  }

  // Fallback to string conversion
  return String(value);
}
